import { useId, type FormHTMLAttributes, type HTMLAttributes, type ReactNode } from "react";

import { cn } from "../lib/cn";

export function CreationFormLayout({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cn("w-full max-w-2xl divide-y divide-border md:max-w-210", className)}
    />
  );
}

export function CreationFormSection({
  id,
  title,
  titleClassName,
  children,
}: {
  id?: string;
  title: string;
  titleClassName?: string;
  children: ReactNode;
}) {
  const headingId = useId();
  const labelledBy = id ? `${id}-title` : headingId;
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className="grid scroll-mt-4 items-start gap-3 py-6 md:grid-cols-[9rem_minmax(0,1fr)] md:gap-6"
    >
      <h3 id={labelledBy} className={cn("text-sm font-semibold text-primary", titleClassName)}>
        {title}
      </h3>
      <div className="min-w-0 max-w-2xl space-y-4">{children}</div>
    </section>
  );
}

export function CreationFormActions({ children }: { children: ReactNode }) {
  return (
    <div className="grid gap-3 py-4 md:grid-cols-[9rem_minmax(0,1fr)] md:gap-6">
      <div className="flex min-w-0 max-w-2xl flex-wrap justify-end gap-2 md:col-start-2">
        {children}
      </div>
    </div>
  );
}

/**
 * The standard frame of a creation or settings form: one white `FormPanel` on the page
 * background, sections separated by a single rule, actions below it in a `FormFooter`. The
 * frame is what anchors a form in the interface, so pages do not draw their own. It takes the
 * full width it's given — a page that wants a narrower, centered form sets that on its own
 * `SectionLayout` (`variant="narrow"`) so the page title lines up with the form below it,
 * rather than this component picking its own width independently of the header above it.
 */
export function FormCard({ className, children, ...props }: FormHTMLAttributes<HTMLFormElement>) {
  return (
    <form {...props} className={cn("w-full space-y-4", className)}>
      {children}
    </form>
  );
}

/** The framed panel holding a form's sections; the footer stays outside it. */
export function FormPanel({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("overflow-hidden rounded-panel border border-border bg-surface", className)}>{children}</div>
  );
}

/**
 * One part of a form. From `md` up the title and its one short line sit in a
 * fixed left column and the fields in the right one, so a form reads as a list
 * of decisions; below `md` they stack.
 */
export function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: string;
  /** A few words, not a sentence: what this part decides. */
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "grid gap-x-8 gap-y-4 border-b border-border px-5 py-6 last:border-b-0 md:grid-cols-[13rem_minmax(0,1fr)] md:px-6",
        className,
      )}
    >
      <header>
        <h2 className="text-sm font-semibold text-primary">{title}</h2>
        {description ? <p className="mt-1 text-xs leading-5 text-muted">{description}</p> : null}
      </header>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  );
}

/** The row closing a form, detached below the panel: a quiet recap on the left, the actions on the right. */
export function FormFooter({
  children,
  actions,
  className,
}: {
  children?: ReactNode;
  actions: ReactNode;
  className?: string;
}) {
  return (
    <footer
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0 text-xs leading-5">{children}</div>
      <div className="flex shrink-0 gap-2">{actions}</div>
    </footer>
  );
}
